/**
 * @typedef {Object} ResourcesModel~ResourceItem
 * General
 * @property {string} id
 * @property {string} name
 * @property {string} type
 * @property {boolean} favourite
 * @property {number} index
 * @property {string} dyn_state - whether this resource can be launched
 * @property {boolean} dynamic - is this a resource which can be enabled/disabled dynamically
 *
 * Specifics
 * @property {string} launch_url
 * @property {string} state - for Dynamic Firewall Rules/Apps - whether it is Enabled, Disabled or Enabled with time
 * @property {number} kind_of_application
 * @property {number} protocol_type
 */
const Utils = require("../common/Utils");

/**
 *
 * @constructor
 */
function ResourcesModel() {
    const self = this;
    let resourceData;
    let currentRequest;

    /**
     *
     * @param {string} [key] id of the desired resource
     * @return {ResourcesModel~ResourceItem}
     */
    this.getResource = function(key) {
        return (resourceData) ? resourceData[key] : undefined;
    };

    /**
     *
     * @return {Array.<ResourcesModel~ResourceItem>} resource model
     */
    this.getResources = function() {
        return resourceData || {};
    };

    this.getGuaranteedResources = function() {
        if (resourceData) {
            return $.Deferred().resolve(resourceData).promise();
        }

        if (currentRequest) {
            return currentRequest;
        }

        return self.refresh();
    };

    this.addFavorites = function(keys, doneCallback, failCallback) {
        return _updateFavorites(keys, true, doneCallback, failCallback);
    };

    this.removeFavorites = function(keys, doneCallback, failCallback) {
        return _updateFavorites(keys, false, doneCallback, failCallback);
    };

    this.addResource = function(resource) {
        resourceData[resource.id] = resource;

        const serverIs7orGreater = Utils.isGreaterThanOrEqualToServerVersion(CudaLaunch.serverConfiguration.version, "7.0.0");
        postProcessResource(resourceData[resource.id], 0, serverIs7orGreater);
    };

    this.removeResource = function(key) {
        delete resourceData[key];
    };

    this.reset = function() {
        resourceData = {};
        $.event.trigger("resourceslistchange", [resourceData]);
    };

    /**
     * The result of this merge will be objects in newArray with any additional properties from the objects in oldArray
     * where objects share the same id property.
     * e.g.
     * mergeArraysOfObjectsById([{id:1, b:b1, c:c1}, {id:2, b:b2, c:c2}], [{id:1, b:bb1, d:d1}, {id:3, b:b3, c:c3}])
     * will return
     * [{id:1, b:bb1, c:c1, d:d1}, {id:3, b:b3, c:c3}]
     */
    function mergeArraysOfObjectsById(oldArray, newArray) {
        const result = [];

        for (let i = 0; i < newArray.length; i++) {
            const newId = newArray[i].id;

            if (newId === undefined) {
                continue;
            }
            let found = false;

            for (let j = 0; j < oldArray.length; j++) {
                const oldId = oldArray[j].id;

                if (oldId !== newId) {
                    continue;
                }
                found = true;
                result.push($.extend({}, oldArray[j], newArray[i]));
                break;
            }

            if (!found) {
                result.push($.extend({}, newArray[i]));
            }
        }
        return result;
    }

    this.refresh = function() {
        const serverIs7orGreater = Utils.isGreaterThanOrEqualToServerVersion(CudaLaunch.serverConfiguration.version, "7.0.0");
        const npSupported = Utils.isGreaterThanOrEqualToServerVersion(CudaLaunch.serverConfiguration.version, "7.0.1");
        const fwRulesReturnedAsResources = Utils.isGreaterThanOrEqualToServerVersion(CudaLaunch.serverConfiguration.version, "7.2.2");

        // Fetch the resource information
        console.debug("refreshing resources model");

        const deferredArray = [];
        deferredArray.push(new $.Deferred().resolve()); // Ensure there's at least 1 resolved deferred in the array

        currentRequest = $.ajax({
            url : "/sslvpn_api/resource",
            cache : false
        }).done((response) => {
            // Iterate over returned objects and update resourceData
            const oldResourceData = resourceData || {};
            resourceData = {};

            for (let i = 0; i < response.length; i++) {
                const resourceId = response[i].id;

                // We can't just do a deep mergeBy(id) here as we want to exclude any objects contained within arrays that are not in the response
                if (oldResourceData[resourceId]) {
                    resourceData[resourceId] = {};
                    const mergedKeys = {};

                    for (let respResKey in response[i]) {
                        if (response[i].hasOwnProperty(respResKey)) {
                            resourceData[resourceId][respResKey] = {};
                            const respResVal = response[i][respResKey];

                            if (typeof respResVal === "object"
                                    && respResVal.constructor === Array
                                    && oldResourceData[resourceId][respResKey]) {
                                // Need to merge each object in this array if the id is the same
                                resourceData[resourceId][respResKey] = mergeArraysOfObjectsById(oldResourceData[resourceId][respResKey], respResVal);
                            } else {
                                // Nothing to merge, use new value
                                resourceData[resourceId][respResKey] = respResVal;
                            }
                            mergedKeys[respResKey] = 1;
                        }
                    }

                    // Add any client-side data that wasn't updated
                    for (let oldResKey in oldResourceData[resourceId]) {
                        if (oldResourceData[resourceId].hasOwnProperty(oldResKey) && !mergedKeys.hasOwnProperty(oldResKey)) {
                            resourceData[resourceId][oldResKey] = oldResourceData[resourceId][oldResKey];
                        }
                    }
                } else {
                    // This is a new new resource so nothing to merge with
                    resourceData[resourceId] = $.extend({}, response[i]);
                }
            }

            let pfIndex = 0;
            const ruleResources = {};

            for (let resourceKey in resourceData) {
                if (resourceData.hasOwnProperty(resourceKey)) {
                    const resource = resourceData[resourceKey];
                    pfIndex = postProcessResource(resourceData[resourceKey], pfIndex, serverIs7orGreater);

                    // Remove network place resources if we don't support them for this server
                    if (!npSupported && resource.type === "network_place") {
                        delete resourceData[resourceKey];
                    } else if (resource.type === "firewall_ruleset") {
                        if (!fwRulesReturnedAsResources) {
                            if (resource.id) {
                                deferredArray.push(
                                    $.ajax({
                                        url : "/sslvpn_api/resource/rulesets/" + resource.id,
                                        cache : false
                                    }).done((response) => {
                                        const rules = {};

                                        for (let i = 0; i < response.length; i++) {
                                            const rule = $.extend({
                                                type : "firewall_rule",
                                                name : response[i].id,
                                                favourite : false,
                                                favouritable : false,
                                                ruleset : resource.id,
                                                dynamic : false,
                                                dyn_state : "Enabled"
                                            },
                                            response[i],
                                            {id : "firewall_rule_" + response[i].id}
                                            );
                                            setResourceHash(rule);
                                            rules[rule.id] = rule;
                                        }
                                        $.extend(ruleResources, rules);
                                    }).fail((data) => {
                                        console.error("unable to retrieve firewall rules from the server, status: " + data.statusText);
                                    })
                                );
                            }
                        }
                        delete resourceData[resourceKey];
                    }
                }
            }

            // In 7.1.0 and above, VPN connection profiles are returned from the resources api endpoint
            if (!Utils.isGreaterThanOrEqualToServerVersion(CudaLaunch.serverConfiguration.version, "7.1.0")) {
                // Fetch vpn connection profile data from server
                deferredArray.push($.ajax({
                    url : "/sslvpn_api/vpn/settings"
                }).fail((jqXHR, textStatus, errorThrown) => {
                    console.error("unable to retrieve VPN profiles from server: " + errorThrown);
                }).done((response) => {
                    // Iterate through response to add values to resourceData
                    for (let i = 0; i < response.length; i++) {
                        // Find the resource with matching id/name and merge with vpn connection response
                        if (resourceData[response[i].name]) {
                            resourceData[response[i].name] = $.extend(
                                {readonly : true},
                                response[i],
                                resourceData[response[i].name]); // Resource is last parameter to preserve the name
                        }
                    }
                })
                );
            }

            $.when.apply($, deferredArray).always(() => {
                console.debug("resources refreshed OK");

                // Now all the Dynamic Firewall Rules are in we can order them and add them to the resources object
                const orderedRules = {};
                Object.keys(ruleResources).sort().forEach((ruleKey) => {
                    orderedRules[ruleKey] = ruleResources[ruleKey];
                });
                $.extend(resourceData, orderedRules);

                $.event.trigger("resourceslistchange", [resourceData]);
            });
        }).fail((data) => {
            console.error("unable to retrieve resource from the server, status: " + data.statusText);
            $.event.trigger("resourcesfetchfailure", [data.status, data.statusText]);
        }).always(() => {
            currentRequest = undefined;
        });

        return currentRequest;
    };

    this.updateResource = function(resourceId) {
        console.debug("updating resource " + resourceId);
        return $.ajax({
            url : "/sslvpn_api/resource/" + resourceId + "?_=" + new Date().getTime(),
            type : "GET",
            cache : false
        }).done((response) => {
            let pfIndex = 0;

            if (resourceData[resourceId].tunnelPath && resourceData[resourceId].tunnelPath.length > 0) {
                // Pre-7 launch paths had an index at the end, so grab it if it exists, to use in post processing
                const match = resourceData[resourceId].tunnelPath.match(/\d+$/);

                if (match) {
                    pfIndex = parseInt(match[0], 10);
                }
            }
            const serverIs7orGreater = Utils.isGreaterThanOrEqualToServerVersion(CudaLaunch.serverConfiguration.version, "7.0.0");

            for (let i = 0; i < response.length; i++) {
                $.extend(resourceData[response[i].id], response[i]);
                pfIndex = postProcessResource(resourceData[response[i].id], pfIndex, serverIs7orGreater);
            }
        });
    };

    function postProcessResource(resource, pfIndex, serverIs7orGreater) {
        let rv = pfIndex;
        setResourceHash(resource);

        if (resource.dynamic === undefined) {
            resource.dynamic = false;
        }

        if (resource.dyn_state === undefined) {
            resource.dyn_state = "Enabled";
        }

        // We need to construct the launch path for tunnels differently for new and old servers
        if (resource.type === "port_forward") {
            resource.tunnelPath = "/vpn/" + resource.id;

            if (!serverIs7orGreater) {
                resource.tunnelPath += "/" + resource.forwarded_service + pfIndex;
                rv++;
            }
        } else if (resource.type === "explicit_port_forward") {
            if (!serverIs7orGreater && resource.kind_of_application === 2) {
                // This is a tunneled web forward, so mangle it into something usable
                resource.type = "port_forward";
                resource.forwarded_service = "WEB";
                resource.additional_fields = {
                    protocol : (resource.protocol_type === 1) ? "HTTP" : "HTTPS"
                };
                const pf = resource.port_forwards[0];
                resource.local_port = pf.local_port;
                resource.tunnelPath = "/vpn/" + pf.id + "/Explicit";
            } else {
                resource.port_forwards.forEach((pf) => {
                    pf.tunnelPath = "/vpn/" + pf.id;

                    if (!serverIs7orGreater) {
                        pf.tunnelPath += "/Explicit";
                    }
                });
            }
        }

        return rv;
    }

    function setResourceHash(resource) {
        let name = resource.id;
        let hash = 0;

        // Strip off the resource type prefix (if it has one)
        if (resource.id.indexOf(resource.type) === 0) {
            name = resource.id.substring(resource.type.length + 1);
        }

        // Strip off the resource subtype prefix (if it has one)
        if (name.indexOf(resource.subtype) === 0) {
            name = name.substring(resource.subtype.length + 1);
        }

        if (name.length > 0) {
            for (let i = 0; i < name.length; i++) {
                hash = ((hash << 5) - hash) + name.charCodeAt(i);
                hash |= 0;
            }
        }
        resource._hash = Math.abs(hash);
    }

    function _updateFavorites(keys, isAdd, doneCallback, failCallback) {
        const resources = [];

        for (let i = 0; i < keys.length; i++) {
            const resourceItem = self.getResource(keys[i]);
            resources.push({
                resourceId : resourceItem.id,
                resourceTypeId : resourceItem.type
            });
        }

        const params = {changes : JSON.stringify(resources)};
        params["_method"] = isAdd ? "PUT" : "DELETE";

        console.debug("updating favorites");
        return $.ajax({
            type : "POST",
            url : "/sslvpn_api/resource/favorite",
            data : params
        }).done(() => {
            console.debug("favorites updated OK");

            for (let i = 0; i < keys.length; i++) {
                const resourceItem = self.getResource(keys[i]);
                resourceItem.favourite = isAdd;
            }

            $.event.trigger("resourceslistchange", [resourceData]);

            if (doneCallback) {
                doneCallback();
            }
        }).fail((data) => {
            console.error("unable to update favorites, status: " + data.statusText);

            if (failCallback) {
                failCallback(data.statusText);
            }
        });
    }

    // BNNGS-1176 When we (re)initialize the resources model fire an event to refresh all the views
    $.event.trigger("resourceslistchange", [{}]);
}

module.exports = ResourcesModel;